import React, { useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// BUFL TV — featured video block for the Home page.
// Detects YouTube (watch / live / shorts / youtu.be) and Facebook video URLs
// and renders a lazy-loaded iframe. Any other URL falls back to a generic
// iframe embed (users can still surprise us with a Vimeo link).
// The iframe is only mounted when the section scrolls into view, so mobile
// users on 3G don't pay the cost of the embed until they actually reach it.
// ---------------------------------------------------------------------------

const YOUTUBE_RE = /(?:v=|\/live\/|\/embed\/|\/shorts\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/;

function toYoutubeEmbed(url) {
  const m = (url || "").match(YOUTUBE_RE);
  if (!m) return url;
  // Don't autoplay — the user explicitly clicks play (mobile-friendly, no
  // sound surprise). modestbranding hides most of the YouTube branding.
  return `https://www.youtube.com/embed/${m[1]}?modestbranding=1&rel=0`;
}

function toFacebookEmbed(url) {
  return (
    "https://www.facebook.com/plugins/video.php?href=" +
    encodeURIComponent(url) +
    "&show_text=false"
  );
}

export function resolveEmbed(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) {
    return { type: "youtube", src: toYoutubeEmbed(url) };
  }
  if (u.includes("facebook.com") || u.includes("fb.watch")) {
    return { type: "facebook", src: toFacebookEmbed(url) };
  }
  return { type: "iframe", src: url };
}

export default function FeaturedVideo({ url, title }) {
  const embed = resolveEmbed(url);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || visible) return;
    // Use IntersectionObserver so the iframe is only mounted when the block
    // scrolls into view. rootMargin gives a small pre-load window so the
    // embed is ready by the time the user actually looks at it.
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
          break;
        }
      }
    }, { rootMargin: "200px 0px" });
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, [visible]);

  if (!embed) return null;

  return (
    <section
      className="border-t border-neutral-900 py-14 md:py-20"
      data-testid="bufl-tv-section"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-[10px] font-stats uppercase tracking-[0.4em] text-gold mb-2">
              BUFL TV
            </p>
            <h2
              className="font-display font-black text-white uppercase tracking-tight text-3xl md:text-5xl"
              data-testid="bufl-tv-title"
            >
              {title?.trim() ? title : "Vidéo à la une"}
            </h2>
          </div>
        </div>

        <div
          ref={containerRef}
          className="relative w-full aspect-video bg-black border border-neutral-900 overflow-hidden"
          data-testid="bufl-tv-embed-wrapper"
        >
          {visible ? (
            <iframe
              src={embed.src}
              title={title || "BUFL TV"}
              loading="lazy"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              data-testid="bufl-tv-iframe"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-neutral-600 font-stats text-xs uppercase tracking-widest">
                Chargement de la vidéo…
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
